import React, {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import Quill from 'quill';

type QuillSource = 'api' | 'silent' | 'user';

type RangeStatic = {
  index: number;
  length: number;
} | null;

type QuillBounds = HTMLElement | string;
type ToolbarHandlerContext = {
  quill: Quill;
};
type ToolbarHandler = (
  this: ToolbarHandlerContext,
  value: unknown
) => void;
type ToolbarModuleOptions = Record<string, unknown> & {
  container?: unknown;
  handlers?: Record<string, ToolbarHandler>;
};
type ToolbarControl = string | Record<string, unknown>;

export type UnprivilegedEditor = {
  getHTML: () => string;
  getLength: () => number;
  getText: (index?: number, length?: number) => string;
  getContents: (index?: number, length?: number) => unknown;
  getSelection: (focus?: boolean) => RangeStatic;
};

export interface ReactQuillProps {
  id?: string;
  className?: string;
  style?: CSSProperties;
  theme?: string;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  readOnly?: boolean;
  modules?: Record<string, unknown>;
  formats?: string[];
  bounds?: QuillBounds;
  scrollingContainer?: QuillBounds;
  tabIndex?: number;
  preserveWhitespace?: boolean;
  onChange?: (
    value: string,
    delta: unknown,
    source: QuillSource,
    editor: UnprivilegedEditor
  ) => void;
  onChangeSelection?: (
    range: RangeStatic,
    source: QuillSource,
    editor: UnprivilegedEditor
  ) => void;
  onFocus?: (
    range: RangeStatic,
    source: QuillSource,
    editor: UnprivilegedEditor
  ) => void;
  onBlur?: (
    previousRange: RangeStatic,
    source: QuillSource,
    editor: UnprivilegedEditor
  ) => void;
}

export type ReactQuillRef = {
  blur: () => void;
  focus: () => void;
  getEditor: () => Quill | null;
};

const normalizeHtml = (value?: string) => {
  if (!value || value === '<p><br></p>') {
    return '';
  }

  return value;
};

const getEditorHtml = (editor: Quill) => normalizeHtml(editor.root.innerHTML);
const normalizeFormats = (formats?: string[]) =>
  formats?.filter(format => format !== 'bullet' && format !== 'direction');
const removeDirectionControls = (toolbar: unknown) => {
  if (!Array.isArray(toolbar)) {
    return toolbar;
  }

  return toolbar
    .map(group => {
      if (!Array.isArray(group)) {
        return group;
      }

      return group.filter((control: ToolbarControl) => {
        if (typeof control === 'string') {
          return control !== 'direction';
        }

        const format = Object.keys(control)[0];
        return format !== 'direction';
      });
    })
    .filter(group => !Array.isArray(group) || group.length > 0);
};
const getFormatsForRange = (editor: Quill, range: RangeStatic) => {
  if (!range) {
    return {};
  }

  return editor.getFormat(range);
};
const SAFE_TOOLBAR_HANDLERS: Record<string, ToolbarHandler> = {
  direction(value) {
    if (value === 'rtl' || value === false) {
      this.quill.format('align', false, Quill.sources.USER);
      this.quill.format('direction', false, Quill.sources.USER);
      return;
    }

    this.quill.format('direction', false, Quill.sources.USER);
  },
  indent(value) {
    const formats = getFormatsForRange(this.quill, this.quill.getSelection());
    const indent = parseInt(String(formats.indent || 0), 10);

    if (value === '+1' || value === '-1') {
      let modifier = value === '+1' ? 1 : -1;

      if (formats.direction === 'rtl') {
        modifier *= -1;
      }

      this.quill.format('indent', indent + modifier, Quill.sources.USER);
    }
  },
  list(value) {
    const formats = getFormatsForRange(this.quill, this.quill.getSelection());

    if (value === 'check') {
      if (formats.list === 'checked' || formats.list === 'unchecked') {
        this.quill.format('list', false, Quill.sources.USER);
      } else {
        this.quill.format('list', 'unchecked', Quill.sources.USER);
      }
    } else {
      this.quill.format('list', value, Quill.sources.USER);
    }
  },
};
const withSafeToolbarHandlers = (modules?: Record<string, unknown>) => {
  if (!modules || modules.toolbar == null || modules.toolbar === false) {
    return modules;
  }

  const toolbar = modules.toolbar;

  if (
    Array.isArray(toolbar) ||
    typeof toolbar === 'string' ||
    toolbar instanceof HTMLElement
  ) {
    return {
      ...modules,
      toolbar: {
        container: removeDirectionControls(toolbar),
        handlers: SAFE_TOOLBAR_HANDLERS,
      },
    };
  }

  if (typeof toolbar === 'object') {
    const toolbarOptions = toolbar as ToolbarModuleOptions;

    return {
      ...modules,
      toolbar: {
        ...toolbarOptions,
        container: removeDirectionControls(toolbarOptions.container),
        handlers: {
          ...SAFE_TOOLBAR_HANDLERS,
          ...(toolbarOptions.handlers ?? {}),
        },
      },
    };
  }

  return modules;
};

const createUnprivilegedEditor = (editor: Quill): UnprivilegedEditor => ({
  getHTML: () => getEditorHtml(editor),
  getLength: () => editor.getLength(),
  getText: (index, length) => editor.getText(index, length),
  getContents: (index, length) => editor.getContents(index, length),
  getSelection: (focus?: boolean) => editor.getSelection(focus),
});

const setEditorContents = (editor: Quill, value?: string) => {
  const normalizedValue = normalizeHtml(value);

  if (!normalizedValue) {
    editor.setContents([]);
    return;
  }

  editor.clipboard.dangerouslyPasteHTML(normalizedValue, 'api');
};

function ReactQuillComponent(
  {
    id,
    className,
    style,
    theme = 'snow',
    value,
    defaultValue,
    placeholder,
    readOnly = false,
    modules,
    formats,
    bounds,
    scrollingContainer,
    tabIndex,
    preserveWhitespace,
    onChange,
    onChangeSelection,
    onFocus,
    onBlur,
  }: ReactQuillProps,
  ref: ForwardedRef<ReactQuillRef>
) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);
  const valueRef = useRef<string>(normalizeHtml(value ?? defaultValue));

  useImperativeHandle(
    ref,
    () => ({
      blur: () => {
        quillRef.current?.blur();
      },
      focus: () => {
        quillRef.current?.focus();
      },
      getEditor: () => quillRef.current,
    }),
    []
  );

  useEffect(() => {
    if (!hostRef.current || quillRef.current) {
      return;
    }

    const resolvedModules = withSafeToolbarHandlers(modules);
    const resolvedFormats = normalizeFormats(formats);
    const editorElement = document.createElement(
      preserveWhitespace ? 'pre' : 'div'
    );

    if (typeof tabIndex === 'number') {
      editorElement.tabIndex = tabIndex;
    }

    hostRef.current.appendChild(editorElement);

    const quill = new Quill(editorElement, {
      bounds,
      formats: resolvedFormats,
      modules: resolvedModules,
      placeholder,
      readOnly,
      theme,
    });

    quillRef.current = quill;
    setEditorContents(quill, value ?? defaultValue);

    const handleTextChange = (
      delta: unknown,
      _oldDelta: unknown,
      source: QuillSource
    ) => {
      const nextValue = getEditorHtml(quill);
      valueRef.current = nextValue;
      onChange?.(nextValue, delta, source, createUnprivilegedEditor(quill));
    };

    const handleSelectionChange = (
      range: RangeStatic,
      oldRange: RangeStatic,
      source: QuillSource
    ) => {
      const editor = createUnprivilegedEditor(quill);

      onChangeSelection?.(range, source, editor);

      if (range && !oldRange) {
        onFocus?.(range, source, editor);
      }

      if (!range && oldRange) {
        onBlur?.(oldRange, source, editor);
      }
    };

    quill.on('text-change', handleTextChange);
    quill.on('selection-change', handleSelectionChange);

    return () => {
      quill.off('text-change', handleTextChange);
      quill.off('selection-change', handleSelectionChange);
      quillRef.current = null;
      hostRef.current?.replaceChildren();
    };
  }, [
    bounds,
    defaultValue,
    formats,
    modules,
    onBlur,
    onChange,
    onChangeSelection,
    onFocus,
    placeholder,
    preserveWhitespace,
    readOnly,
    tabIndex,
    theme,
    value,
  ]);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) {
      return;
    }

    quill.enable(!readOnly);
  }, [readOnly]);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) {
      return;
    }

    const normalizedValue = normalizeHtml(value);
    if (normalizedValue === valueRef.current) {
      return;
    }

    const currentSelection = quill.getSelection();
    setEditorContents(quill, normalizedValue);
    valueRef.current = normalizeHtml(getEditorHtml(quill));

    if (currentSelection) {
      quill.setSelection(currentSelection, 'silent');
    }
  }, [value]);

  return <div id={id} className={className} style={style} ref={hostRef} />;
}

const ReactQuill = forwardRef(ReactQuillComponent);

ReactQuill.displayName = 'ReactQuill';

export default ReactQuill;
